// ─── Customer Quote API Service — READDY CUSTOMER QUOTE API CONTRACT (1:1) ───
// 10 endpoints wired to the actual Cursor Backend customer-facing routes.
// No client-side pricing. No direct DB reads. No Admin API calls.

import { getSupabaseClient } from '@/lib/supabase';
import type {
  CustomerOk,
  CustomerErr,
  QuotePreviewRequest,
  QuoteCreateRequest,
  CustomerPreviewData,
  CustomerQuoteCreateData,
  CustomerQuoteListData,
  CustomerQuoteDetailData,
  CustomerQuoteStatusData,
  CustomerPdfData,
  CustomerFinalOfferData,
  CustomerFinishingMethodsData,
  CustomerQuoteCurrenciesData,
} from '@/lib/quotationTypes';

// ─── API base construction (GBCOSPACK ECS API server, not SPA origin) ───

function getApiOrigin(): string {
  const base = (import.meta.env.VITE_API_BASE_URL || '')
    .trim()
    .replace(/\/$/, '');
  if (!base) {
    throw new Error(
      'Missing VITE_API_BASE_URL. Customer Quote API requires an absolute GBCOSPACK API base URL.',
    );
  }
  if (!/^https?:\/\//i.test(base)) {
    throw new Error(
      `VITE_API_BASE_URL must be an absolute http(s) URL (got "${base}").`,
    );
  }
  return base;
}

function getCustomerApiBase(): string {
  return `${getApiOrigin()}/api/customer`;
}

// ─── Auth helper ───

async function getAuthHeaders(): Promise<Record<string, string>> {
  const supabase = getSupabaseClient();
  if (!supabase) return {};
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

// ─── Fetch wrapper with envelope parsing ───

class QuoteApiError extends Error {
  code: string;
  status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = 'QuoteApiError';
  }
}

async function customerFetch<T>(
  url: string,
  init?: { method?: string; body?: string; headers?: Record<string, string> },
): Promise<T> {
  const supabase = getSupabaseClient();
  const token = supabase
    ? (await supabase.auth.getSession()).data.session?.access_token
    : undefined;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers as Record<string, string> || {}),
  };

  const res = await fetch(url, {
    ...init,
    headers,
  });

  // Attempt JSON parse regardless of status (Backend returns envelope JSON for errors too)
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // Not valid JSON
    throw new QuoteApiError('UNKNOWN_ERROR', `HTTP ${res.status}`, res.status);
  }

  // Detect envelope shape
  const envelope = body as Record<string, unknown>;
  const isOk =
    envelope.success === true &&
    envelope.ok === true &&
    'data' in envelope;

  if (isOk) {
    return (envelope as CustomerOk<T>).data;
  }

  // Error envelope
  const err = envelope as CustomerErr;
  const code = typeof err.code === 'string' ? err.code : 'UNKNOWN_ERROR';
  const message = typeof err.message === 'string' ? err.message : 'Request failed.';
  throw new QuoteApiError(code, message, res.status);
}

export { QuoteApiError };

/** Map a Backend Quote API error code to a customer-safe Korean message. */
export function quoteErrorMessage(code: string, fallback: string): string {
  switch (code) {
    case 'PRODUCT_PRICE_NOT_CONFIGURED':
      return '선택한 수량에 대한 공식 견적단가가 아직 등록되지 않았습니다.';
    case 'FINISHING_PRICE_NOT_CONFIGURED':
      return '선택한 후가공에 대한 공식 견적단가가 아직 등록되지 않았습니다.';
    case 'FINISHING_METHOD_NOT_FOUND':
      return '선택한 후가공의 견적 정보를 확인할 수 없습니다. 잠시 후 다시 시도해주세요.';
    case 'INVALID_QUOTE_TYPE':
      return '유효하지 않은 견적 유형입니다.';
    case 'PDF_NOT_READY':
      return '견적서 PDF를 아직 생성하고 있습니다. 잠시 후 다시 확인해주세요.';
    default:
      return fallback;
  }
}

// ─── Endpoints ───

/** 1. Preview quote (POST /api/customer/preliminary-quotes/preview) */
export async function previewQuote(
  request: QuotePreviewRequest,
): Promise<CustomerPreviewData> {
  const url = `${getCustomerApiBase()}/preliminary-quotes/preview`;
  return customerFetch<CustomerPreviewData>(url, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/** 2. Create preliminary quote (POST /api/customer/preliminary-quotes) — HTTP 201 */
export async function createQuote(
  request: QuoteCreateRequest,
): Promise<CustomerQuoteCreateData> {
  const url = `${getCustomerApiBase()}/preliminary-quotes`;
  return customerFetch<CustomerQuoteCreateData>(url, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/** 3. List my quotes (GET /api/customer/preliminary-quotes) */
export async function listQuotes(
  page = 1,
  pageSize = 20,
): Promise<CustomerQuoteListData> {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(1, page)));
  qs.set('pageSize', String(Math.min(50, Math.max(1, pageSize))));
  const url = `${getCustomerApiBase()}/preliminary-quotes?${qs.toString()}`;
  return customerFetch<CustomerQuoteListData>(url, { method: 'GET' });
}

/** 4. Quote detail (GET /api/customer/preliminary-quotes/[id]) */
export async function getQuoteDetail(
  quoteId: string,
): Promise<CustomerQuoteDetailData> {
  const url = `${getCustomerApiBase()}/preliminary-quotes/${encodeURIComponent(quoteId)}`;
  return customerFetch<CustomerQuoteDetailData>(url, { method: 'GET' });
}

/** 5. Request Final Offer (POST /api/customer/preliminary-quotes/[id]/request-final-offer) */
export async function requestFinalOffer(
  quoteId: string,
): Promise<CustomerQuoteStatusData> {
  const url = `${getCustomerApiBase()}/preliminary-quotes/${encodeURIComponent(quoteId)}/request-final-offer`;
  return customerFetch<CustomerQuoteStatusData>(url, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** 6. Preliminary Quote PDF (GET /api/customer/preliminary-quotes/[id]/pdf)
 *  @deprecated Legacy V2 signed-URL flow. V3 uses getPreliminaryQuotePdfBlob (pdf-stream).
 *  Removable after successful V3 authenticated E2E verification. */
export async function getQuotePdf(
  quoteId: string,
): Promise<CustomerPdfData> {
  const url = `${getCustomerApiBase()}/preliminary-quotes/${encodeURIComponent(quoteId)}/pdf`;
  return customerFetch<CustomerPdfData>(url, { method: 'GET' });
}

/** 7. Final Offer detail (GET /api/customer/final-offers/[id]) */
export async function getFinalOffer(
  offerId: string,
): Promise<CustomerFinalOfferData> {
  const url = `${getCustomerApiBase()}/final-offers/${encodeURIComponent(offerId)}`;
  return customerFetch<CustomerFinalOfferData>(url, { method: 'GET' });
}

/** 8. Final Offer PDF (GET /api/customer/final-offers/[id]/pdf)
 *  @deprecated Legacy V2 signed-URL flow. V3 uses getFinalOfferPdfBlob (pdf-stream).
 *  Removable after successful V3 authenticated E2E verification. */
export async function getFinalOfferPdf(
  offerId: string,
): Promise<CustomerPdfData> {
  const url = `${getCustomerApiBase()}/final-offers/${encodeURIComponent(offerId)}/pdf`;
  return customerFetch<CustomerPdfData>(url, { method: 'GET' });
}

/** 9. Finishing Methods — public (GET /api/customer/finishing-quote-methods) */
export async function getFinishingQuoteMethods(): Promise<CustomerFinishingMethodsData> {
  const url = `${getCustomerApiBase()}/finishing-quote-methods`;
  // Public endpoint: no auth required, but pass token if available
  return customerFetch<CustomerFinishingMethodsData>(url, { method: 'GET' });
}

/** 10. Quote Currencies — public (GET /api/customer/quote-currencies) */
export async function getQuoteCurrencies(): Promise<CustomerQuoteCurrenciesData> {
  const url = `${getCustomerApiBase()}/quote-currencies`;
  return customerFetch<CustomerQuoteCurrenciesData>(url, { method: 'GET' });
}

// ─── PDF Binary Stream (Quotation V3) ───
// Backend generates the official PDF on demand and returns raw application/pdf
// bytes. There is NO JSON success envelope and NO signed URL for V3 stream.
// These helpers perform an authenticated binary fetch and return a Blob.

export interface PdfBlobResult {
  blob: Blob;
  filename: string | null;
  contentType: string | null;
}

/** Parse a Content-Disposition header for a safe filename. */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return star[1].trim().replace(/^"|"$/g, '');
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  if (plain?.[1]) return plain[1].trim();
  return null;
}

/** Resolve a Backend-provided stream path (relative or absolute) to a full URL. */
function resolvePdfStreamUrl(
  pdfStreamPath: string | null | undefined,
  fallbackPath: string,
): string {
  const origin = getApiOrigin();
  if (pdfStreamPath) {
    if (/^https?:\/\//i.test(pdfStreamPath)) return pdfStreamPath;
    if (pdfStreamPath.startsWith('/')) return `${origin}${pdfStreamPath}`;
    return `${origin}/${pdfStreamPath.replace(/^\/+/, '')}`;
  }
  return fallbackPath;
}

/** Authenticated binary fetch. Returns Blob + parsed filename. Throws on non-2xx. */
async function fetchPdfStream(
  url: string,
  disposition: 'inline' | 'attachment',
): Promise<PdfBlobResult> {
  const supabase = getSupabaseClient();
  const token = supabase
    ? (await supabase.auth.getSession()).data.session?.access_token
    : undefined;

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const sep = url.includes('?') ? '&' : '?';
  const res = await fetch(`${url}${sep}disposition=${disposition}`, { headers });

  const contentType = res.headers.get('content-type') || null;

  if (!res.ok) {
    // Backend may return a JSON error envelope on failure. Do NOT treat as PDF.
    let code = 'PDF_GENERATION_FAILED';
    let message = 'PDF generation failed. Please try again.';
    try {
      if (contentType && contentType.includes('application/json')) {
        const body = (await res.json()) as Record<string, unknown>;
        if (typeof body.code === 'string') code = body.code;
        if (typeof body.message === 'string') message = body.message;
      }
    } catch {
      // non-JSON error body — keep generic message
    }
    throw new QuoteApiError(code, message, res.status);
  }

  const blob = await res.blob();
  const filename = parseContentDispositionFilename(res.headers.get('content-disposition'));
  return { blob, filename, contentType };
}

/** Preliminary Quote PDF stream (GET .../pdf-stream). Prefer Backend pdfStreamPath. */
export async function getPreliminaryQuotePdfBlob(
  quoteId: string,
  disposition: 'inline' | 'attachment' = 'inline',
  pdfStreamPath?: string | null,
): Promise<PdfBlobResult> {
  const fallback = `${getCustomerApiBase()}/preliminary-quotes/${encodeURIComponent(quoteId)}/pdf-stream`;
  return fetchPdfStream(resolvePdfStreamUrl(pdfStreamPath, fallback), disposition);
}

/** Final Offer PDF stream (GET .../pdf-stream). Prefer Backend pdfStreamPath. */
export async function getFinalOfferPdfBlob(
  offerId: string,
  disposition: 'inline' | 'attachment' = 'inline',
  pdfStreamPath?: string | null,
): Promise<PdfBlobResult> {
  const fallback = `${getCustomerApiBase()}/final-offers/${encodeURIComponent(offerId)}/pdf-stream`;
  return fetchPdfStream(resolvePdfStreamUrl(pdfStreamPath, fallback), disposition);
}

// ─── PDF Binary as ArrayBuffer (PDF.js input) ───
// Reuses the existing authenticated blob stream fetch and converts to ArrayBuffer,
// so PDF.js never calls the Backend endpoint directly (spec: fetch → ArrayBuffer → PDF.js).

export interface PdfArrayBufferResult {
  arrayBuffer: ArrayBuffer;
  filename: string | null;
  contentType: string | null;
}

/** Preliminary Quote PDF as ArrayBuffer for PDF.js rendering. */
export async function getPreliminaryQuotePdfArrayBuffer(
  quoteId: string,
  disposition: 'inline' | 'attachment' = 'inline',
  pdfStreamPath?: string | null,
): Promise<PdfArrayBufferResult> {
  const { blob, filename, contentType } = await getPreliminaryQuotePdfBlob(
    quoteId,
    disposition,
    pdfStreamPath,
  );
  return { arrayBuffer: await blob.arrayBuffer(), filename, contentType };
}

/** Final Offer PDF as ArrayBuffer for PDF.js rendering. */
export async function getFinalOfferPdfArrayBuffer(
  offerId: string,
  disposition: 'inline' | 'attachment' = 'inline',
  pdfStreamPath?: string | null,
): Promise<PdfArrayBufferResult> {
  const { blob, filename, contentType } = await getFinalOfferPdfBlob(
    offerId,
    disposition,
    pdfStreamPath,
  );
  return { arrayBuffer: await blob.arrayBuffer(), filename, contentType };
}

/** Trigger a browser download for a Blob via a temporary object URL (authenticated binary fetch reuse). */
export function downloadBlob(blob: Blob, filename: string): void {
  const safe = filename.replace(/[\\/:*?"<>|]/g, '_').trim() || 'quotation.pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safe;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}